// AuthContext.js (veya AuthContext.jsx)
import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import Services from "./services/Services";
import { normalizeCompanyTypes } from "./utils/transportBidFlow";
import {
  hasCustomerWarehouseEntry,
  hasPendingCustomerWarehouseOperation,
} from "./utils/customerWarehouseEntries";
import { normalizeFrontAuth } from "./utils/frontAuth";

const AuthContext = createContext(null);

// Zorunlu evrak tipleri artık /flow/get-documents-list servisinden (is_mandatory=1) geliyor.
// Bu liste yalnızca servis erişilemezse fallback olarak kullanılır; Yetki/Faaliyet Belgesi
// zorunlu olmadığından burada yer almaz.
const REQUIRED_MERCHANT_DOCUMENT_TYPES = [
  "ticaret-sicil-gazetesi",
  "imza-sirkuleri",
  "vergi-levhasi",
  "kimlik-belgesi-on",
  "kimlik-belgesi-arka",
];

function pickCompanyIDFromUserInfos(result) {
  if (!result) return null;

  if (Array.isArray(result)) {
    if (result.length === 0) return null;
    return pickCompanyIDFromUserInfos(result[0]);
  }

  // result string ise (sadece role dönüyorsa) companyID yok demektir
  if (typeof result !== "object") return null;

  const unwrapId = (value) => {
    if (!value) return null;
    if (typeof value === "object") {
      return (
        value.companyID ??
        value.companyId ??
        value.company_id ??
        value.corporationID ??
        value.corporationId ??
        value.corporation_id ??
        value.id ??
        null
      );
    }
    return value;
  };

  // Öncelik: corporationInfos içinden (bazı endpoint'lerde corporationID ile farklı gelebiliyor)
  const corpInfosRaw = result.corporationInfos;
  const corpInfos = Array.isArray(corpInfosRaw) ? corpInfosRaw?.[0] : corpInfosRaw;
  const fromCorpInfos =
    unwrapId(corpInfos?.company_id) ??
    unwrapId(corpInfos?.companyID) ??
    unwrapId(corpInfos?.companyId) ??
    unwrapId(corpInfos?.corporationID) ??
    unwrapId(corpInfos?.id) ??
    null;
  const fromCorpInfosStr = String(fromCorpInfos ?? "").trim();
  if (fromCorpInfosStr) return fromCorpInfosStr;

  const picked =
    unwrapId(result.corporationID) ??
    unwrapId(result.corporationId) ??
    unwrapId(result.corporation_id) ??
    unwrapId(result.companyID) ??
    unwrapId(result.companyId) ??
    unwrapId(result.company_id) ??
    unwrapId(result.warehouseCompanyID) ??
    unwrapId(result.warehouseCompanyId) ??
    unwrapId(result.warehouse_company_id) ??
    unwrapId(result.merchantCompanyID) ??
    unwrapId(result.merchantCompanyId) ??
    unwrapId(result.merchant_company_id) ??
    null;

  const s = String(picked ?? "").trim();
  return s ? s : null;
}

export function AuthProvider({ children }) {
  const [userType, setUserType] = useState(null);
  const [user, setUser] = useState(null);

  // sözleşme durumu: "1" (onaylı), "3" (onay bekliyor), diğer/null (kilit)
  const [agreementStatus, setAgreementStatus] = useState(null);
  const [merchantDocumentsApproved, setMerchantDocumentsApproved] = useState(false);
  // /flow/check-company-documents-complete sonucu (isComplete). Kilit bu değere göre belirlenir.
  const [documentsComplete, setDocumentsComplete] = useState(false);
  // Aynı yanıtın kırılımı: { documents, agreements, signature, bank } — ana sayfa bandı
  // hangi başlığın eksik olduğunu yazabilsin diye. Okunamadıysa null.
  const [complianceBreakdown, setComplianceBreakdown] = useState(null);
  // Uyum bilgisi ÇEKİLEMEDİĞİNDE (ağ/servis hatası) kilit uygulanmaz: tek bir başarısız
  // istek merchant'ı tüm panelden kilitliyordu. Gerçek yetki kontrolü sunucuda.
  const [complianceUnknown, setComplianceUnknown] = useState(false);

  // merchant için companyID'yi auth seviyesinde tutuyoruz
  const [merchantCompanyID, setMerchantCompanyID] = useState(null);
  const [merchantCompanyTypes, setMerchantCompanyTypes] = useState([]);
  const [merchantRole, setMerchantRole] = useState(null);
  // merchant sayfa/aksiyon/kapsam yetkileri (frontAuth alanı). GetUserInfos'tan hydrate edilir.
  const [merchantFrontAuth, setMerchantFrontAuth] = useState(null);
  const [customerHasWarehouseEntry, setCustomerHasWarehouseEntry] = useState(null);
  // Zorunlu onay kilidi (müşteri): KVKK / Kullanıcı Sözleşmesi güncel sürümüyle onaylı değilse
  // dolu gelir; panel kilitlenir (sidebar Anasayfa'ya iner, rotalar anasayfaya yönlenir).
  // Metin GÜNCELLENİNCE sürüm (md5) değiştiği için onay BE'de otomatik düşer ve kilit yeniden devreye girer.
  const [customerMissingConsents, setCustomerMissingConsents] = useState([]);

  // Admin alt-rolü (superadmin / admin / customer-relations) ve sayfa/aksiyon yetkileri.
  // /flow/get-user-infos yanıtından hydrate edilir.
  const [frontAuthRole, setFrontAuthRole] = useState(null);
  const [frontAuth, setFrontAuth] = useState(null);

  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);

  const token =
    typeof window !== "undefined"
      ? localStorage.getItem("token") || sessionStorage.getItem("token")
      : null;

  const parseUserType = (res) => {
    if (res?.data?.status === true && typeof res?.data?.result === "string") return res.data.result;
    return null;
  };

  // Üst üste binen hydrate çağrılarında GEÇ dönen ESKİ yanıt yeni durumu ezmesin diye
  // her çağrıya sıra numarası verilir (servis fonksiyonları AbortSignal desteklemiyor).
  const complianceRequestRef = useRef(0);

  const fetchMerchantComplianceStatus = useCallback(
    async (signal) => {
      if (!token) return;
      const requestId = ++complianceRequestRef.current;
      const isStale = () => signal?.aborted || complianceRequestRef.current !== requestId;

      try {
        // 1) companyID bul
        // NOT: bu servis fonksiyonları AbortSignal desteklemiyor; iptal yerine yukarıdaki
        // requestId (isStale) koruması kullanılır — fazladan argüman geçmiyoruz.
        const infoRes = await Services.GetUserInfos(token);
        const infoResult = infoRes?.data?.result;
        const companyID = pickCompanyIDFromUserInfos(infoResult);
        const infoObj = Array.isArray(infoResult) ? infoResult?.[0] : infoResult;
        const role = infoObj?.role ?? null;

        if (isStale()) return;
        setMerchantCompanyID(companyID || null);
        setMerchantRole(role);
        setMerchantFrontAuth(normalizeFrontAuth(infoObj?.frontAuth));
        if (!companyID) {
          // companyID yoksa durumu çekemeyiz → kilitli kabul edilecek (bu bir HATA değil,
          // firmasız merchant durumu; "bilinmiyor" bayrağı temizlenir).
          setComplianceUnknown(false);
          setAgreementStatus(null);
          setMerchantDocumentsApproved(false);
          setDocumentsComplete(false);
          setMerchantCompanyTypes([]);
          return;
        }

        // 2) firma tipi + agreements + documents + zorunlu liste + tamlık kontrolü çek
        const [companyRes, agRes, docRes, docListRes, completeRes] = await Promise.all([
          Services.GetWarehouseCompany(token, companyID),
          Services.GetWarehouseCompanyAgreements(token, companyID),
          Services.GetWarehouseCompanyDocuments(token, companyID),
          Services.GetDocumentsList(token),
          Services.CheckCompanyDocumentsComplete(token),
        ]);

        if (isStale()) return;
        // Tamlık okuması SOFT hata da dönebilir (HTTP 200 + {status:false}); bu "evrak eksik"
        // DEĞİL, "okunamadı" demektir. Eskiden isComplete=false'a düşüp evrakları tam olan
        // merchant'ı tüm panelden kilitliyordu. Artık yalnızca status=true yanıt kesin kabul
        // edilir, aksi halde durum "bilinmiyor" işaretlenir (bkz. catch'teki fail-open notu).
        // NOT: firmasız merchant yukarıdaki companyID dalında zaten kilitli kalır.
        const isCompleteKnown = Boolean(completeRes?.data?.status);
        setComplianceUnknown(!isCompleteKnown);

        if (companyRes?.data?.status) {
          const companyResult = companyRes?.data?.result;
          const company = Array.isArray(companyResult) ? companyResult?.[0] : companyResult;
          setMerchantCompanyTypes(normalizeCompanyTypes(company?.type));
        } else {
          setMerchantCompanyTypes([]);
        }

        // isComplete: kilit bu değere göre belirlenir
        const isComplete = Boolean(isCompleteKnown && completeRes?.data?.result?.isComplete);
        setDocumentsComplete(isComplete);

        // Kırılım: ana sayfadaki uyarı bandı "neyin eksik olduğunu" kalem kalem yazabilsin diye.
        // isComplete tek başına hangi başlığın eksik olduğunu söylemiyordu; bant da bu yüzden
        // imza/kaşe ya da banka eksikken bile "evraklarınız eksik" diyordu.
        const breakdown = isCompleteKnown ? (completeRes?.data?.result || {}) : null;
        setComplianceBreakdown(
          breakdown
            ? {
                documents: Boolean(breakdown.documentsComplete),
                agreements: Boolean(breakdown.agreementsComplete),
                signature: Boolean(breakdown.signatureComplete),
                // Eski backend bu alanı döndürmüyorsa eksik SAYMA (fail-open).
                bank: breakdown.bankComplete === undefined ? true : Boolean(breakdown.bankComplete),
              }
            : null
        );

        if (agRes?.data?.status && Array.isArray(agRes?.data?.result) && agRes.data.result.length > 0) {
          const latest = [...agRes.data.result].sort(
            (a, b) => Number(b?.createdDateTime || 0) - Number(a?.createdDateTime || 0)
          )[0];

          const code = String(latest?.status || "").trim() || null;
          setAgreementStatus(code);
        } else {
          setAgreementStatus(null);
        }

        // Zorunlu evrak tipleri öncelikle servisten; yoksa fallback liste
        let requiredDocTypes = [];
        if (docListRes?.data?.status && Array.isArray(docListRes?.data?.result)) {
          const docList = docListRes.data.result;
          requiredDocTypes = docList
            .filter((d) => String(d?.is_mandatory ?? "") === "1")
            .map((d) => d?.type)
            .filter(Boolean);
          if (requiredDocTypes.length === 0) {
            requiredDocTypes = docList.map((d) => d?.type).filter(Boolean);
          }
        }
        if (requiredDocTypes.length === 0) requiredDocTypes = REQUIRED_MERCHANT_DOCUMENT_TYPES;

        if (docRes?.data?.status && Array.isArray(docRes?.data?.result)) {
          const latestByType = {};
          for (const item of docRes.data.result) {
            const type = item?.type;
            if (!type) continue;
            const prev = latestByType[type];
            if (!prev) latestByType[type] = item;
            else {
              const prevTime = Number(prev?.createdDateTime) || 0;
              const curTime = Number(item?.createdDateTime) || 0;
              if (curTime > prevTime) latestByType[type] = item;
            }
          }

          const allApproved =
            requiredDocTypes.length > 0 &&
            requiredDocTypes.every((t) => {
              const doc = latestByType[t];
              return String(doc?.status ?? "").trim() === "1";
            });
          setMerchantDocumentsApproved(allApproved);
        } else {
          setMerchantDocumentsApproved(false);
        }
      } catch (err) {
        if (isStale()) return;
        // FAIL-OPEN: uyum durumu ÇEKİLEMEDİ demek "evraklar eksik" demek değildir. Eskiden
        // bu blok documentsComplete=false yazıp merchant'ı tüm panelden kilitliyor, üstelik
        // hatayı yutup status="ready" ile devam ediyordu (kullanıcıya mesaj/yeniden deneme yok).
        // Artık durum "bilinmiyor" işaretlenir; kilit yalnız KESİN olarak eksik olduğunda uygulanır.
        setComplianceUnknown(true);
      }
    },
    [token]
  );

  const hydrateAdminFrontAuth = useCallback(
    async (signal) => {
      if (!token) return;
      try {
        const res = await Services.GetUserInfos(token);
        if (signal?.aborted) return;
        const result = res?.data?.result;
        const info = Array.isArray(result) ? result?.[0] : result;
        setFrontAuthRole(info?.role ?? null);
        setFrontAuth(normalizeFrontAuth(info?.frontAuth));
      } catch (err) {
        if (signal?.aborted) return;
        setFrontAuthRole(null);
        setFrontAuth(null);
      }
    },
    [token]
  );

  const hydrateFromService = useCallback(
    async (signal) => {
      if (!token) {
        setStatus("ready");
        setUserType(null);
        setUser(null);
        setAgreementStatus(null);
        setMerchantDocumentsApproved(false);
        setDocumentsComplete(false);
        setMerchantCompanyID(null);
        setMerchantCompanyTypes([]);
        setMerchantFrontAuth(null);
        setCustomerHasWarehouseEntry(null);
        setFrontAuthRole(null);
        setFrontAuth(null);
        return;
      }

      setStatus("loading");
      setError(null);

      try {
        // 1) role çek
        const res = await Services.GetUserType(token, { signal });
        const typeFromApi = parseUserType(res);

        setUserType(typeFromApi);
        setUser(null);

        // 2) merchant ise sözleşme+evrak; admin ise frontAuth (rol+yetki) hydrate et
        if (typeFromApi === "merchant") {
          await fetchMerchantComplianceStatus(signal);
          setFrontAuthRole(null);
          setFrontAuth(null);
        } else {
          setAgreementStatus(null);
          setMerchantDocumentsApproved(false);
          setDocumentsComplete(false);
          setMerchantCompanyID(null);
          setMerchantCompanyTypes([]);
          setMerchantRole(null);
          setMerchantFrontAuth(null);

          if (typeFromApi === "admin") {
            await hydrateAdminFrontAuth(signal);
          } else {
            setFrontAuthRole(null);
            setFrontAuth(null);
          }
        }

        setStatus("ready");
      } catch (err) {
        if (signal?.aborted) return;
        setError(err);
        setUserType(null);
        setUser(null);
        setAgreementStatus(null);
        setMerchantDocumentsApproved(false);
        setDocumentsComplete(false);
        setMerchantCompanyID(null);
        setMerchantCompanyTypes([]);
        setMerchantRole(null);
        setMerchantFrontAuth(null);
        setCustomerHasWarehouseEntry(null);
        setFrontAuthRole(null);
        setFrontAuth(null);
        setStatus("error");
      }
    },
    [token, fetchMerchantComplianceStatus, hydrateAdminFrontAuth]
  );

  useEffect(() => {
    const ctrl = new AbortController();
    hydrateFromService(ctrl.signal);
    return () => ctrl.abort();
  }, [hydrateFromService]);

  useEffect(() => {
    if (userType !== "customer") {
      setCustomerHasWarehouseEntry(null);
      return undefined;
    }

    const customerID =
      typeof window !== "undefined"
        ? localStorage.getItem("userID") || sessionStorage.getItem("userID")
        : null;

    if (!token || !customerID) {
      setCustomerHasWarehouseEntry(false);
      return undefined;
    }

    let isCancelled = false;
    setCustomerHasWarehouseEntry(null);

    // Menü iki koşuldan BİRİ sağlanınca açılır:
    //   (1) tamamlanmış/çıkılmış bir depo girişi snapshot'ı var (eski kural), ya da
    //   (2) teklifi onaylanmış ama henüz tamamlanmamış bir depo girişi operasyonu var.
    // İkincisi olmadan sayfa, teklif onayı ile operasyonun tamamlanması arasındaki tüm
    // süre boyunca menüde görünmüyordu.
    Promise.allSettled([
      Services.GetOperationSnapshots(token, { customerID }),
      Services.GetOperations(token, { customerID }),
    ])
      .then(([snapRes, opsRes]) => {
        if (isCancelled) return;
        const snapValue = snapRes?.status === "fulfilled" ? snapRes.value : null;
        const snapshots =
          snapValue?.data?.status && Array.isArray(snapValue?.data?.result)
            ? snapValue.data.result
            : [];
        const opsValue = opsRes?.status === "fulfilled" ? opsRes.value : null;
        const operations = Array.isArray(opsValue?.data?.result) ? opsValue.data.result : [];
        setCustomerHasWarehouseEntry(
          hasCustomerWarehouseEntry(snapshots, customerID)
            || hasPendingCustomerWarehouseOperation(operations)
        );
      })
      .catch(() => {
        if (isCancelled) return;
        setCustomerHasWarehouseEntry(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [token, userType]);

  const logout = useCallback(() => {
    setUserType(null);
    setUser(null);
    setAgreementStatus(null);
    setMerchantDocumentsApproved(false);
    setDocumentsComplete(false);
    setMerchantCompanyID(null);
    setMerchantCompanyTypes([]);
    setMerchantRole(null);
    setMerchantFrontAuth(null);
    setCustomerHasWarehouseEntry(null);
    setFrontAuthRole(null);
    setFrontAuth(null);
    setStatus("ready");
  }, []);

  const refreshUser = useCallback(() => {
    const ctrl = new AbortController();
    hydrateFromService(ctrl.signal);
    return () => ctrl.abort();
  }, [hydrateFromService]);

  const isAgreementApproved = useMemo(() => {
    const code = String(agreementStatus ?? "").trim();
    return code === "1";
  }, [agreementStatus]);

  // Sadece merchant için: check-company-documents-complete isComplete=false ise kilitli
  const isContractLocked = useMemo(() => {
    if (userType !== "merchant") return false;
    if (complianceUnknown) return false; // durum bilinmiyorsa kilitleme (bkz. fail-open notu)
    return !documentsComplete;
  }, [userType, documentsComplete, complianceUnknown]);

  // Uyarı: isComplete false ise; ya da isComplete true olsa bile evrak/sözleşmelerden
  // onaylanmamış (yüklenmemiş veya onay bekleyen) varsa "Lütfen evrak ve sözleşmelerinizi kontrol ediniz."
  const showMerchantComplianceWarning = useMemo(() => {
    if (userType !== "merchant") return false;
    const allApproved = isAgreementApproved && merchantDocumentsApproved;
    return !documentsComplete || !allApproved;
  }, [userType, documentsComplete, isAgreementApproved, merchantDocumentsApproved]);

  // Zorunlu onay durumu (KVKK, Kullanıcı Sözleşmesi …). MÜŞTERİ ve MERCHANT için geçerlidir;
  // onay verilince anasayfa "consents:updated" yayınlar ve burası tazelenir.
  const refreshCustomerConsents = useCallback(() => {
    if (!["customer", "merchant"].includes(userType) || !token) {
      setCustomerMissingConsents([]);
      return;
    }
    Services.GetUserConsents(token, {})
      .then((res) => {
        const consents = res?.data?.status && Array.isArray(res?.data?.result?.consents)
          ? res.data.result.consents
          : [];
        setCustomerMissingConsents(consents.filter((c) => c?.required && !c?.accepted));
      })
      .catch(() => {});
  }, [userType, token]);

  useEffect(() => { refreshCustomerConsents(); }, [refreshCustomerConsents]);

  useEffect(() => {
    const handler = () => refreshCustomerConsents();
    window.addEventListener("consents:updated", handler);
    return () => window.removeEventListener("consents:updated", handler);
  }, [refreshCustomerConsents]);

  // Zorunlu onaylar verilmeden panel kilitlenir (müşteri + merchant).
  // İsim geriye dönük uyum için "customer" ile başlıyor; kapsam artık her iki tip.
  const isCustomerConsentLocked =
    ["customer", "merchant"].includes(userType) && customerMissingConsents.length > 0;

  const value = useMemo(
    () => ({
      userType,
      setUserType,
      user,
      setUser,
      logout,
      refreshUser,
      status,
      error,

      agreementStatus,
      setAgreementStatus,
      isContractLocked,
      isAgreementApproved,
      merchantDocumentsApproved,
      setMerchantDocumentsApproved,
      documentsComplete,
      complianceBreakdown,
      showMerchantComplianceWarning,

      merchantCompanyID,
      merchantCompanyTypes,
      merchantRole,
      merchantFrontAuth,
      customerHasWarehouseEntry,
      customerMissingConsents,
      isCustomerConsentLocked,

      frontAuthRole,
      frontAuth,
    }),
    [
      userType,
      user,
      logout,
      refreshUser,
      status,
      error,
      agreementStatus,
      isContractLocked,
      isAgreementApproved,
      merchantDocumentsApproved,
      documentsComplete,
      complianceBreakdown,
      showMerchantComplianceWarning,
      merchantCompanyID,
      merchantCompanyTypes,
      merchantRole,
      merchantFrontAuth,
      customerHasWarehouseEntry,
      customerMissingConsents,
      isCustomerConsentLocked,
      frontAuthRole,
      frontAuth,
    ]
  );

  if (status === "loading" && userType === null && user === null) return null;

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
